import {
  type ApolloCache,
  type ApolloClient,
  type ApolloError,
  type NormalizedCacheObject,
  useApolloClient,
} from '@apollo/client';
import type { Education } from '@seek/ca-myprofile-utils';
import { useTranslations } from '@vocab/react';
import { useDispatch } from 'react-redux';

import { CREATE_RESUME_PAGE } from 'src/components/Router';
import { usePage } from 'src/components/Router/usePage';
import { useQuery, useToastMutation } from 'src/modules/apollo';
import { getDataFromCache } from 'src/modules/apollo/apolloCache';
import { useAppConfig } from 'src/modules/appConfig';
import type { ActionName, Viewer } from 'src/types';
import {
  GetConfirmedQualificationsDocument,
  GetJobsDbConfirmedQualificationsDocument,
  GetUnconfirmedQualificationsDocument,
} from 'src/types/graphql';
import * as Schema from 'src/types/graphql';

import { useCandidateId } from '../data';

import translations from './.vocab';
import {
  addUnconfirmedQualification,
  addUnconfirmedQualificationFailure,
  addUnconfirmedQualificationSuccess,
  rejectUnconfirmedQualification,
  rejectUnconfirmedQualificationFailure,
  rejectUnconfirmedQualificationSuccess,
  removeQualification,
  removeQualificationFailure,
  removeQualificationSuccess,
  updateQualification,
  updateQualificationFailure,
  updateQualificationSuccess,
  updateVerifiedQualification,
  updateVerifiedQualificationFailure,
  updateVerifiedQualificationSuccess,
} from './actionCreators';
import {
  generateConfirmQualificationOptimisticResponse,
  generateDeleteQualificationOptimisticResponse,
  generateRejectUnconfirmedDataOptimisticResponse,
  generateUpdateQualificationOptimisticResponse,
} from './apolloCache.optimistic';
import {
  updateCacheConfirmJobsDbQualification,
  updateCacheConfirmQualification,
  updateCacheDeleteQualification,
  updateCacheRejectQualification,
  updateCacheUpdateQualification,
  updateCacheUpdateVerifiedQualification,
} from './apolloCache.updates';
import {
  getConfirmedQualifications,
  getJobsDbConfirmedQualifications,
  getUnconfirmedQualifications,
} from './qualification.graphql';

export const useGetUnconfirmedQualifications = () => {
  const { showFeature, language, zone, isUnifiedSite } = useAppConfig();

  const showResumeParsing = showFeature('resume-parsing');

  const page = usePage();
  const isCreateResumePage = page === CREATE_RESUME_PAGE;
  const shouldSkip = !showResumeParsing || isCreateResumePage;

  const { data, loading, error } = useQuery<
    Viewer<{ unconfirmedQualifications: Education[] }>
  >(getUnconfirmedQualifications, {
    skip: shouldSkip,
    bypassErrorPageOnFail: true,
    variables: {
      languageCode: language,
      zone,
      isUnifiedSite,
    },
  });

  const unconfirmedQualifications =
    data?.viewer?.unconfirmedQualifications ?? [];

  return {
    unconfirmedQualifications,
    loading,
    error,
  };
};

export const getConfirmedQualificationsFromCache = <
  T extends Record<string, any>,
>({
  cache,
  languageCode,
  showQualificationLevel2,
  zone,
  isUnifiedSite,
}: {
  cache: ApolloCache<T>;
  showQualificationLevel2: boolean;
} & Omit<Schema.ConfirmQualificationMutationVariables, 'qualification'>) => {
  const confirmedQualificationsData = getDataFromCache({
    cache,
    query: showQualificationLevel2
      ? GetJobsDbConfirmedQualificationsDocument
      : GetConfirmedQualificationsDocument,
    variables: { languageCode, zone, isUnifiedSite },
  });

  return {
    confirmedQualifications:
      (confirmedQualificationsData?.viewer
        ?.confirmedQualifications as unknown as Education[]) ?? [],
  };
};

export const useGetConfirmedQualifications = () => {
  const { showFeature, language, zone, isUnifiedSite } = useAppConfig();

  const showQualificationLevel2 = showFeature('qualification-level-major');

  const gqlQuery = showQualificationLevel2
    ? getJobsDbConfirmedQualifications
    : getConfirmedQualifications;

  const { data, loading, error } = useQuery<
    Viewer<{ confirmedQualifications: Education[] }>
  >(gqlQuery, {
    variables: {
      languageCode: language,
      zone,
      isUnifiedSite,
    },
  });

  const confirmedQualifications = data?.viewer?.confirmedQualifications || [];

  return {
    confirmedQualifications,
    loading,
    error,
  };
};

export const getUnconfirmedQualificationsFromCache = ({
  apolloClient,
  languageCode,
  zone,
  isUnifiedSite,
}: {
  apolloClient: ApolloClient<NormalizedCacheObject | Record<string, unknown>>;
} & Omit<Schema.ConfirmQualificationMutationVariables, 'qualification'>) => {
  const unconfirmedQualificationsData = getDataFromCache({
    cache: apolloClient.cache,
    query: GetUnconfirmedQualificationsDocument,
    variables: { languageCode, zone, isUnifiedSite },
  });

  const unconfirmedQualifications =
    (unconfirmedQualificationsData?.viewer
      ?.unconfirmedQualifications as unknown as Education[]) || [];

  return {
    unconfirmedQualifications,
  };
};

const key = 'education';

export const useUpdateQualification = (action: ActionName) => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();
  const { showFeature } = useAppConfig();

  const onCompleted = () => {
    dispatch(updateQualificationSuccess());
  };
  const onError = (error: ApolloError) => {
    dispatch(updateQualificationFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.UpdateQualificationDocument, {
    key,
    errorActionRouteParameters: { card: 'education', mode: 'READ' },
    onMutate: ({ qualification }) => {
      dispatch(updateQualification(qualification, action));
    },
    onCompleted,
    onError,
    successToastMessage:
      action === 'ADD' ? t('Education added') : t('Education updated'),
    update: updateCacheUpdateQualification,
    optimisticResponse: generateUpdateQualificationOptimisticResponse({
      apolloClient,
      _id: candidateId || '',
      showQualificationLevel2: showFeature('qualification-level-major'),
    }),
  });
};

export const useUpdateQualificationJobsDb = (action: ActionName) => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();
  const { showFeature } = useAppConfig();

  const onCompleted = () => {
    dispatch(updateQualificationSuccess());
  };
  const onError = (error: ApolloError) => {
    dispatch(updateQualificationFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.UpdateQualificationJobsDbDocument, {
    key,
    errorActionRouteParameters: { card: 'education', mode: 'READ' },
    onMutate: ({ qualification }) => {
      dispatch(updateQualification(qualification, action));
    },
    onCompleted,
    onError,
    successToastMessage:
      action === 'ADD' ? t('Education added') : t('Education updated'),
    update: updateCacheUpdateQualification,
    // Typing this is tricky and provides
    // little value to the codebase so the error has been ignored
    // @ts-ignore
    optimisticResponse: generateUpdateQualificationOptimisticResponse({
      apolloClient,
      _id: candidateId || '',
      showQualificationLevel2: showFeature('qualification-level-major'),
    }),
  });
};

export const useDeleteQualification = () => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();
  const { showFeature } = useAppConfig();

  const onMutate = () => {
    dispatch(removeQualification());
  };

  const onCompleted = () => {
    dispatch(removeQualificationSuccess());
  };

  const onError = (error: ApolloError) => {
    dispatch(removeQualificationFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.DeleteQualificationDocument, {
    key,
    errorActionRouteParameters: { card: 'education', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Education removed'),
    update: updateCacheDeleteQualification,
    optimisticResponse: generateDeleteQualificationOptimisticResponse({
      _id: candidateId || '',
      apolloClient,
      showQualificationLevel2: showFeature('qualification-level-major'),
    }),
  });
};

export const useDeleteQualificationJobsDb = () => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();
  const { showFeature } = useAppConfig();

  const onMutate = () => {
    dispatch(removeQualification());
  };

  const onCompleted = () => {
    dispatch(removeQualificationSuccess());
  };

  const onError = (error: ApolloError) => {
    dispatch(removeQualificationFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.DeleteQualificationJobsDbDocument, {
    key,
    errorActionRouteParameters: { card: 'education', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Education removed'),
    update: updateCacheDeleteQualification,
    optimisticResponse: generateDeleteQualificationOptimisticResponse({
      _id: candidateId || '',
      apolloClient,
      showQualificationLevel2: showFeature('qualification-level-major'),
    }),
  });
};

export const useAddUnconfirmedQualification = (action: ActionName) => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();
  const { showFeature } = useAppConfig();

  const onMutate = (
    { qualification }: Schema.ConfirmQualificationMutationVariables,
    mutationMetadata?: { contentRead: boolean },
  ) => {
    dispatch(
      addUnconfirmedQualification(
        { id: qualification.id },
        action,
        mutationMetadata?.contentRead,
      ),
    );
  };

  const onCompleted = () => {
    dispatch(addUnconfirmedQualificationSuccess());
  };
  const onError = (error: ApolloError) => {
    dispatch(addUnconfirmedQualificationFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.ConfirmQualificationDocument, {
    key,
    errorActionRouteParameters: { card: 'education', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Education added'),
    update: updateCacheConfirmQualification,
    optimisticResponse: generateConfirmQualificationOptimisticResponse({
      apolloClient,
      _id: candidateId || '',
      showQualificationLevel2: showFeature('qualification-level-major'),
    }),
  });
};
export const useAddUnconfirmedQualificationJobsDb = (action: ActionName) => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();
  const { showFeature } = useAppConfig();

  const onMutate = (
    { qualification }: Schema.ConfirmQualificationJobsDbMutationVariables,
    mutationMetadata?: { contentRead: boolean },
  ) => {
    dispatch(
      addUnconfirmedQualification(
        { id: qualification.id },
        action,
        mutationMetadata?.contentRead,
      ),
    );
  };

  const onCompleted = () => {
    dispatch(addUnconfirmedQualificationSuccess());
  };
  const onError = (error: ApolloError) => {
    dispatch(addUnconfirmedQualificationFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.ConfirmQualificationJobsDbDocument, {
    key,
    errorActionRouteParameters: { card: 'education', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Education added'),
    update: updateCacheConfirmJobsDbQualification,
    // Ignore the type error due to JobsDb and Non-JobsDb type issue.
    // @ts-ignore
    optimisticResponse: generateConfirmQualificationOptimisticResponse({
      apolloClient,
      _id: candidateId || '',
      showQualificationLevel2: showFeature('qualification-level-major'),
    }),
  });
};

export const useUpdateVerifiedQualification = () => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();

  const onMutate = () => {
    dispatch(updateVerifiedQualification());
  };
  const onCompleted = () => {
    dispatch(updateVerifiedQualificationSuccess());
  };
  const onError = (error: ApolloError) => {
    dispatch(updateVerifiedQualificationFailure(error));
  };

  return useToastMutation(Schema.UpdateVerifiedQualificationDocument, {
    key,
    errorActionRouteParameters: { card: 'education', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Education updated'),
    update: updateCacheUpdateVerifiedQualification,
  });
};

export const useRejectUnconfirmedQualification = () => {
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();

  const onMutate = ({
    qualification,
  }: Schema.RejectQualificationMutationVariables) => {
    dispatch(rejectUnconfirmedQualification({ id: qualification.id }));
  };

  const onCompleted = () => {
    dispatch(rejectUnconfirmedQualificationSuccess());
  };

  const onError = (error: ApolloError) => {
    dispatch(rejectUnconfirmedQualificationFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.RejectQualificationDocument, {
    key,
    errorActionRouteParameters: { card: 'education', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    update: updateCacheRejectQualification,
    optimisticResponse: generateRejectUnconfirmedDataOptimisticResponse({
      _id: candidateId || '',
      apolloClient,
    }),
  });
};
